import RGSProvider from "./RGS/RGSProvider";

let RGS = null;

const getRGS = async () => {
  if (!RGS) {
    try {
      RGS = RGSProvider.loadProvider();
    } catch (error) {
      throw error;
    }
  }
  return RGS;
};

export const handleInitGame = async (useApiData = false, ignoreFreeRoundsStorage = false) => {
  try {
    const rgs = await getRGS();
    const result = await rgs.initGame(useApiData, ignoreFreeRoundsStorage);
    return result;
  } catch (error) {
    throw error;
  }
};

export const handleGetSpinParams = (universalState) => {
  // RGS is already loaded by the time any spin happens (synchronous access)
  if (!RGS) {
    throw new Error("RGS not initialized. Call handleInitGame first.");
  }
  return RGS.getSpinParams(universalState);
};

export const handleGetBalanceStrategy = () => {
  // RGS is already loaded by the time any spin happens (synchronous access)
  if (!RGS) {
    throw new Error("RGS not initialized. Call handleInitGame first.");
  }
  return RGS.getBalanceStrategy();
};

export const handleShouldSyncBalanceOnWinClose = () => {
  // RGS is already loaded by the time any win animation happens (synchronous access)
  if (!RGS) {
    return true; // Fallback: sync if RGS not initialized
  }
  return RGS.shouldSyncBalanceOnWinClose?.() ?? true;
};

export const handleSpin = async (bet, token, command, purchasedFeature, options) => {
  const rgs = await getRGS();
  try {
    const result = await rgs.spin(bet, token, command, purchasedFeature, options);
    return result;
  } catch (error) {
    throw error;
  }
};

export const handleBuyFeature = async (bet, level) => {
  const rgs = await getRGS();
  return await rgs.buyFeature(bet, level);
};

export const handleSimulate = async (slotface, bet, token) => {
  const rgs = await getRGS();
  return await rgs.simulate(slotface, bet, token);
};

export const handleGetBalance = async () => {
  const rgs = await getRGS();
  if (!rgs.getBalance) {
    throw new Error("Balance sync not supported by this provider");
  }
  return await rgs.getBalance();
};

export const handleFetchRoundsHistory = async () => {
  const rgs = await getRGS();
  if (!rgs.fetchRoundsHistory) {
    throw new Error("Rounds history not supported by this provider");
  }
  return await rgs.fetchRoundsHistory();
};

export const handleIsRoundsHistoryAvailable = () => {
  // Launcher gate (spec §7): the History entry point only exists when the
  // provider can resolve a history URL. Synchronous — used for UI visibility.
  if (!RGS || !RGS.getRoundsHistoryUrl) return false;
  return RGS.getRoundsHistoryUrl() != null;
};

// Replay session (docs/replay-history-spec.md §0). enterReplayMode (Stage 0)
// only flips the switch on the RGS object — no request goes out from it.
export const handleEnterReplayMode = async (replayUrl) => {
  const rgs = await getRGS();
  if (rgs.enterReplayMode) rgs.enterReplayMode(replayUrl);
};

export const handleExitReplayMode = async () => {
  const rgs = await getRGS();
  if (rgs.exitReplayMode) rgs.exitReplayMode();
};

// Stage 1 — fetch the replay round's step sequence. Must run after
// handleEnterReplayMode has resolved (needs this.replayUrl already set).
export const handleFetchReplayInit = async () => {
  const rgs = await getRGS();
  if (!rgs.fetchReplayInit) {
    throw new Error("Replay init not supported by this provider");
  }
  return await rgs.fetchReplayInit();
};

// Stage 9 — standalone replay link boot (docs/replay-history-spec.md §0).
// Used in place of handleInitGame() when the page itself was launched as a
// replay link — see launcherOptions.isStandaloneReplayMode(). Does the
// equivalent of handleEnterReplayMode()+handleFetchReplayInit() plus the
// full config parse handleInitGame() would otherwise do, in one call.
export const handleInitFromReplayLink = async (replayUrl) => {
  const rgs = await getRGS();
  if (!rgs.initFromReplayLink) {
    throw new Error("Standalone replay link not supported by this provider");
  }
  return await rgs.initFromReplayLink(replayUrl);
};
